"use client";

import React, { useMemo, useDeferredValue, useId } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
// import { tAuth } from '@/lib/i18n/auth';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

export type AuthOverlayType = 'login' | 'logout';

export interface AuthOverlayProps {
  isVisible: boolean;
  type?: AuthOverlayType;
  className?: string;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const AuthOverlay = React.memo<AuthOverlayProps>(({ 
  isVisible, 
  type = 'login', 
  className 
}) => {
  // React 19: Generate stable ID for component instance
  const instanceId = useId();
  
  // React 19: Defer visibility state for smoother transitions
  const deferredIsVisible = useDeferredValue(isVisible);
  
  // React 19: Memoize overlay content
  const overlayContent = useMemo(() => {
    const messages = {
      login: {
        title: 'Signing In',
        subtitle: 'Please wait while we authenticate you',
        description: 'This will only take a moment...'
      },
      logout: {
        title: 'Signing Out',
        subtitle: 'Please wait while we sign you out',
        description: 'Redirecting you to the home page...'
      }
    };

    const currentMessages = messages[type];

    return (
      <div className="flex flex-col items-center justify-center space-y-6 text-center">
        {/* Loading Spinner */}
        <div className="relative">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <div className="absolute inset-0 h-12 w-12 rounded-full border-2 border-primary/20" />
        </div>

        {/* Content */}
        <div className="space-y-4 max-w-md min-w-[300px] mx-auto" style={{ minWidth: '300px' }}>
          <h2 className="text-2xl font-semibold text-foreground sm:text-3xl">
            {currentMessages.title}
          </h2>
          <p className="text-lg text-muted-foreground sm:text-xl">
            {currentMessages.subtitle}
          </p>
          <p className="text-base text-muted-foreground/80 sm:text-lg">
            {currentMessages.description}
          </p>
        </div>

        {/* Progress indicator */}
        <div className="w-64 h-1.5 bg-muted rounded-full overflow-hidden mx-auto" style={{ minWidth: '200px' }}>
          <div className="h-full bg-primary rounded-full animate-progress-fill" />
        </div>
      </div>
    );
  }, [type]);

  // Don't render if not visible (including deferred state)
  if (!deferredIsVisible && !isVisible) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center",
        "bg-background/95 backdrop-blur-sm",
        "transition-opacity duration-300",
        deferredIsVisible ? "opacity-100" : "opacity-0",
        className
      )}
      data-instance-id={instanceId}
      data-overlay-type={type}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${instanceId}-title`}
    >
      <div 
        className="relative p-8 rounded-lg bg-card border shadow-lg min-w-[400px] max-w-lg w-full mx-4 sm:min-w-[500px] sm:max-w-xl lg:min-w-[600px] lg:max-w-2xl"
        id={`${instanceId}-content`}
        style={{ minWidth: '400px' }}
      >
        {overlayContent}
      </div>
    </div>
  );
});

AuthOverlay.displayName = "AuthOverlay";

// =============================================================================
// CONVENIENCE COMPONENTS
// =============================================================================

// Convenience components for cleaner usage
export const LoginOverlay = React.memo<{ isVisible: boolean; className?: string }>(({ 
  isVisible, 
  className 
}) => (
  <AuthOverlay isVisible={isVisible} type="login" className={className} />
));

LoginOverlay.displayName = "LoginOverlay";

export const LogoutOverlay = React.memo<{ isVisible: boolean; className?: string }>(({ 
  isVisible, 
  className 
}) => (
  <AuthOverlay isVisible={isVisible} type="logout" className={className} />
));

LogoutOverlay.displayName = "LogoutOverlay"; 
